import React, { useEffect, useState, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Box, Typography, Button, Chip, IconButton, TextField, Grid,
  Select, MenuItem, FormControl, Alert, Drawer, Divider, Avatar, Skeleton, Tooltip,
} from '@mui/material';
import {
  AddOutlined, CloseOutlined, SaveOutlined, EditOutlined,
  LocationOnOutlined, RecordVoiceOverOutlined,
  ArrowForwardOutlined, DeleteOutlineOutlined, DeleteOutlined,
  ContentCopyOutlined, AddCircleOutlined, RemoveCircleOutlined,
  MyLocationOutlined, AddAPhotoOutlined,  PersonAddOutlined, PhotoCameraOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { v4 as uuid } from 'uuid';
import { api } from '../../../hooks/useApi';
import { PRIMARY, INPUT_SX, FormField } from '../shared';
import { ContentBrowserModal } from '../lobbyBuilderTab/ContentBrowserModal';
import SessionDrawer from './SessionDrawer';

// ── Constants ─────────────────────────────────────────────────────────────────
const TYPE_META = {
  session:    { label: 'Session',    color: PRIMARY,   bg: alpha('#1075BA', 0.10) },
  keynote:    { label: 'Keynote',    color: '#7c3aed', bg: alpha('#7c3aed', 0.10) },
  workshop:   { label: 'Workshop',   color: '#d97706', bg: alpha('#d97706', 0.10) },
  break:      { label: 'Break',      color: '#6b7280', bg: alpha('#6b7280', 0.10) },
  networking: { label: 'Networking', color: '#00a882', bg: alpha('#00a882', 0.10) },
  exclusive:  { label: 'Exclusive',  color: '#e11d48', bg: alpha('#e11d48', 0.10) },
};

const WEEKDAY_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTH_NAMES   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const ROW_H_MIN     = 32;
const ROW_H_DEFAULT = 96;
const ROW_H         = ROW_H_DEFAULT;
const COL_W_MIN  = 148;
const TIME_W     = 68;
const HEADER_H   = 66;

const TIME_SLOTS = (() => {
  const s = [];
  for (let h = 1; h <= 24; h++) {
    const hour = h === 24 ? 0 : h;
    const hr   = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? 'AM' : 'PM';
    s.push({ h: hour, m: 0, label: `${hr}:00 ${ampm}`, isNoon: hour === 12 });
  }
  return s;
})();

function timeToSlot(time) {
  if (!time || !time.includes(':')) return null;
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  const baseHour  = h === 0 ? 23 : h - 1;
  const halfSlot  = m >= 30 ? 1 : 0;
  return Math.max(0, Math.min(baseHour * 2 + halfSlot, TIME_SLOTS.length * 2 - 1));
}

function slotToTime(snapSlotIdx) {
  const totalMins = snapSlotIdx * 30 + 60;
  const h = Math.floor(totalMins / 60) % 24;
  const m = totalMins % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}

const TOTAL_SNAPS = 48;
function snapSlotToPct(snapSlotIdx) { return (snapSlotIdx / TOTAL_SNAPS) * 100; }

function isoDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}
function parseLocalDate(iso) {
  if (!iso) return new Date();
  const [y,m,d] = iso.split('-').map(Number);
  return new Date(y, m-1, d);
}

const BLANK = {
  title:'', description:'', dayLabel:'', dayNumber:1,
  startTime:'', endTime:'', timeSlots:[], speakers:[], location:'', type:'session', order:0, posterUrl:'',
};

// ── Serialization helpers ─────────────────────────────────────────────────────

// Collapse speaker objects in local state down to ID strings for the API.
function collapseSpeakers(speakers) {
  return (speakers || [])
    .map(s => (typeof s === 'object' && s !== null ? s.id : s))
    .filter(Boolean);
}

// Prepare the full schedule for the API: collapse speakers → IDs, strip UI-only fields.
function serializeSchedule(schedule) {
  return schedule.map(({ timeSlots, _col, _totalCols, ...item }) => ({
    ...item,
    speakers: collapseSpeakers(item.speakers),
  }));
}


// ── Time slot row (From → To) ─────────────────────────────────────────────────
function TimeSlotRow({ slot, onChange, onDelete }) {
  return (
    <Box sx={{ display:'flex', alignItems:'center', gap:1.5, py:1.25, px:2, borderBottom:'1px solid #f5f7fa','&:last-child':{borderBottom:0} }}>
      <IconButton size="small" onClick={onDelete} sx={{ color:'#c0c8d4','&:hover':{color:'#d32f2f'},flexShrink:0 }}>
        <DeleteOutlineOutlined sx={{ fontSize:18 }} />
      </IconButton>
      <Typography sx={{ fontSize:'0.8rem', color:'#9ea8b3', fontWeight:500, flexShrink:0, width:36 }}>From</Typography>
      <Box sx={{ flex:1, background:'#f8f9fb', borderRadius:2, border:'1px solid #e8edf2', px:1.5, py:0.75 }}>
        <input type="time" value={slot.startTime||''} onChange={e=>onChange({...slot,startTime:e.target.value})}
          style={{ border:'none',background:'transparent',outline:'none',fontSize:'0.875rem',fontWeight:600,color:'#1a1a2e',width:'100%',cursor:'pointer' }} />
      </Box>
      <ArrowForwardOutlined sx={{ fontSize:16, color:'#c0c8d4', flexShrink:0 }} />
      <Typography sx={{ fontSize:'0.8rem', color:'#9ea8b3', fontWeight:500, flexShrink:0, width:20 }}>To</Typography>
      <Box sx={{ flex:1, background:'#f8f9fb', borderRadius:2, border:'1px solid #e8edf2', px:1.5, py:0.75 }}>
        <input type="time" value={slot.endTime||''} onChange={e=>onChange({...slot,endTime:e.target.value})}
          style={{ border:'none',background:'transparent',outline:'none',fontSize:'0.875rem',fontWeight:600,color:'#1a1a2e',width:'100%',cursor:'pointer' }} />
      </Box>
    </Box>
  );
}


// ── Time picker ───────────────────────────────────────────────────────────────
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const val  = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
      const hr   = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      opts.push({ val, label: `${hr}:${String(m).padStart(2,'0')} ${ampm}` });
    }
  }
  return opts;
})();

function fmtTime(t) {
  if (!t || !t.includes(':')) return '';
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return '';
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr}:${String(m).padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function TimePickerPopup({ value, onChange, onClose }) {
  const listRef = React.useRef();
  React.useEffect(() => {
    if (!listRef.current) return;
    const sel = listRef.current.querySelector('[data-selected="true"]');
    if (sel) sel.scrollIntoView({ block: 'center' });
  }, []);
  return (
    <Box sx={{ position:'absolute', top:'100%', left:0, zIndex:1400, width:160, mt:0.5,
      background:'#fff', border:'1px solid #e4e9f0', borderRadius:2, boxShadow:'0 8px 32px rgba(0,0,0,0.12)', overflow:'hidden' }}>
      <Box ref={listRef} sx={{ maxHeight:220, overflowY:'auto',
        '&::-webkit-scrollbar':{width:4}, '&::-webkit-scrollbar-thumb':{background:'#d0d5dd',borderRadius:4} }}>
        {TIME_OPTIONS.map(opt => {
          const selected = opt.val === value;
          return (
            <Box key={opt.val} data-selected={selected} onClick={() => { onChange(opt.val); onClose(); }}
              sx={{ px:2, py:0.9, cursor:'pointer', fontSize:'0.85rem', fontWeight:selected?700:400,
                color:selected?PRIMARY:'#374151', background:selected?alpha(PRIMARY,0.08):'transparent',
                '&:hover':{ background:selected?alpha(PRIMARY,0.12):'#f5f7fa' } }}>
              {opt.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function TimePicker({ label, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef();
  React.useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <Box ref={ref} sx={{ position:'relative', flex:1 }}>
      <Typography sx={{ fontSize:'0.75rem', fontWeight:600, color:'#374151', mb:0.5 }}>{label}</Typography>
      <Box onClick={() => setOpen(o => !o)} sx={{ border:'1px solid #e8edf2', borderRadius:2, background:'#f8f9fb',
        px:1.5, py:0.85, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between',
        '&:hover':{ borderColor:PRIMARY } }}>
        <Typography sx={{ fontSize:'0.82rem', fontWeight:value?600:400, color:value?'#1a1a2e':'#9ea8b3' }}>
          {fmtTime(value) || 'Select time'}
        </Typography>
        <Box component="span" sx={{ fontSize:'0.6rem', color:'#9ea8b3' }}>▾</Box>
      </Box>
      {open && <TimePickerPopup value={value} onChange={onChange} onClose={() => setOpen(false)} />}
    </Box>
  );
}


// ── Session photo field ────────────────────────────────────────────────────────
function SessionPhotoField({ value, onChange }) {
  const [browserOpen, setBrowserOpen] = React.useState(false);
  return (
    <Box sx={{ mb:2 }}>
      <Box onClick={() => setBrowserOpen(true)} sx={{ width:'100%', height:140, borderRadius:2,
        background:value?'transparent':alpha(PRIMARY,0.04), border:value?'none':`2px dashed ${alpha(PRIMARY,0.2)}`,
        overflow:'hidden', cursor:'pointer', position:'relative', '&:hover .photo-overlay':{opacity:1} }}>
        {value
          ? <Box component="img" src={value} alt="session" sx={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
          : <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:1 }}>
              <AddAPhotoOutlined sx={{ fontSize:24, color:alpha(PRIMARY,0.35) }} />
              <Typography variant="caption" sx={{ fontWeight:600, color:alpha(PRIMARY,0.5) }}>Add session photo</Typography>
            </Box>
        }
        <Box className="photo-overlay" sx={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.32)',
          display:'flex', alignItems:'center', justifyContent:'center', opacity:0, transition:'opacity 0.18s ease' }}>
          <AddAPhotoOutlined sx={{ color:'#fff', fontSize:26 }} />
        </Box>
        {value && (
          <Box onClick={e => { e.stopPropagation(); onChange(''); }}
            sx={{ position:'absolute', top:8, right:8, width:24, height:24, borderRadius:'50%',
              background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <CloseOutlined sx={{ fontSize:14, color:'#fff' }} />
          </Box>
        )}
      </Box>
      <ContentBrowserModal open={browserOpen} onClose={() => setBrowserOpen(false)}
        onSelect={url => { onChange(url); setBrowserOpen(false); }} mediaType="image" currentUrl={value||''} />
    </Box>
  );
}


// ── Speaker selector ──────────────────────────────────────────────────────────
const PORTRAIT_RATIO = 3.5 / 4;

// ── URL validation helper ─────────────────────────────────────────────────────
function isValidUrl(str) {
  if (!str) return true;
  try { new URL(str); return true; } catch { return false; }
}

// ── Full speaker editor drawer (ported from SpeakersTab) ──────────────────────
function SpeakerEditorDrawer({ open, speaker, onClose, onSave }) {
  const [form, setForm]               = React.useState({ id: uuid(), name: '', title: '', company: '', bio: '', avatar: '', track: '', order: 0, socialLinks: { linkedin: '', twitter: '', website: '' } });
  const [photoBrowser, setPhotoBrowser] = React.useState(false);
  const [urlErrors, setUrlErrors]       = React.useState({});

  React.useEffect(() => {
    if (!open) return;
    if (speaker) {
      setForm({ id: speaker.id || uuid(), name: '', title: '', company: '', bio: '', avatar: '', track: '', order: 0, socialLinks: { linkedin: '', twitter: '', website: '' }, ...speaker, socialLinks: { linkedin: '', twitter: '', website: '', ...(speaker.socialLinks || {}) } });
    } else {
      setForm({ id: uuid(), name: '', title: '', company: '', bio: '', avatar: '', track: '', order: 0, socialLinks: { linkedin: '', twitter: '', website: '' } });
    }
    setUrlErrors({});
  }, [speaker, open]);

  const set       = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setSocial = (k, v) => {
    setForm(p => ({ ...p, socialLinks: { ...p.socialLinks, [k]: v } }));
    setUrlErrors(e => ({ ...e, [k]: v && !isValidUrl(v) }));
  };

  const hasUrlError = Object.values(urlErrors).some(Boolean);
  const canSave     = form.name.trim() && !hasUrlError;
  const hue         = [...(form.name || 'A')].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 460 }, display: 'flex', flexDirection: 'column', zIndex: 1600 } }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{speaker ? 'Edit Speaker' : 'Add Speaker'}</Typography>
          <IconButton size="small" onClick={onClose}><CloseOutlined sx={{ fontSize: 18 }} /></IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
          {/* Portrait photo */}
          <FormField label="Photo">
            <Box onClick={() => setPhotoBrowser(true)} sx={{
              width: '100%', aspectRatio: '3.5/4', borderRadius: 2, overflow: 'hidden',
              border: '2px dashed #d1d9e6', cursor: 'pointer', position: 'relative',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'border-color 0.15s',
              '&:hover': { borderColor: PRIMARY },
              '&:hover .photo-overlay': { opacity: 1 },
            }}>
              {form.avatar
                ? <Box component="img" src={form.avatar} alt="Speaker photo" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#9ea8b3' }}>
                    <PhotoCameraOutlined sx={{ fontSize: 32 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Click to upload photo</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#b0bac8' }}>400 × 460 recommended</Typography>
                  </Box>
              }
              <Box className="photo-overlay" sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 0.5,
                background: 'rgba(0,0,0,0.45)', opacity: 0, transition: 'opacity 0.15s' }}>
                <PhotoCameraOutlined sx={{ color: '#fff', fontSize: 28 }} />
                <Typography sx={{ color: '#fff', fontSize: '0.75rem', fontWeight: 600 }}>Change photo</Typography>
              </Box>
            </Box>
          </FormField>

          <FormField label="Full Name" required>
            <TextField value={form.name} onChange={e => set('name', e.target.value)}
              fullWidth size="small" placeholder="Jane Doe" sx={INPUT_SX} />
          </FormField>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormField label="Title / Role">
                <TextField value={form.title || ''} onChange={e => set('title', e.target.value)}
                  fullWidth size="small" placeholder="CEO" sx={INPUT_SX} />
              </FormField>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormField label="Company">
                <TextField value={form.company || ''} onChange={e => set('company', e.target.value)}
                  fullWidth size="small" placeholder="GoVirtual Inc." sx={INPUT_SX} />
              </FormField>
            </Box>
          </Box>

          <FormField label="Bio">
            <TextField value={form.bio || ''} onChange={e => set('bio', e.target.value)}
              fullWidth size="small" multiline rows={3} placeholder="Short speaker biography..." sx={INPUT_SX} />
          </FormField>

          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1.5, mt: 0.5, fontSize: '0.875rem' }}>
            Social Links
          </Typography>

          {[
            { key: 'linkedin', label: 'LinkedIn',    placeholder: 'https://linkedin.com/in/...' },
            { key: 'twitter',  label: 'Twitter / X', placeholder: 'https://twitter.com/...' },
            { key: 'website',  label: 'Website',     placeholder: 'https://...' },
          ].map(({ key, label, placeholder }) => (
            <FormField key={key} label={label}>
              <TextField value={form.socialLinks[key] || ''} onChange={e => setSocial(key, e.target.value)}
                fullWidth size="small" placeholder={placeholder}
                error={!!urlErrors[key]} helperText={urlErrors[key] ? 'Enter a valid URL' : ''}
                sx={INPUT_SX} />
            </FormField>
          ))}
        </Box>

        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e8edf2', flexShrink: 0, background: '#fafbfc' }}>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button onClick={() => canSave && onSave(form)} variant="contained" startIcon={<SaveOutlined />}
              disabled={!canSave}
              sx={{ borderRadius: 2, background: PRIMARY, '&:hover': { background: '#0d5fa8' } }}>
              Save Speaker
            </Button>
          </Box>
        </Box>
      </Drawer>

      <ContentBrowserModal open={photoBrowser} onClose={() => setPhotoBrowser(false)}
        onSelect={url => { set('avatar', url); setPhotoBrowser(false); }}
        mediaType="image" currentUrl={form.avatar || ''} />
    </>
  );
}

// ── Compact horizontal speaker card ──────────────────────────────────────────
function SpeakerCard({ speaker, onRemove, onEdit }) {
  const name     = typeof speaker === 'string' ? speaker : speaker.name || '';
  const avatar   = typeof speaker === 'object' ? speaker.avatar : null;
  const title    = typeof speaker === 'object' ? speaker.title : null;
  const company  = typeof speaker === 'object' ? speaker.company : null;
  const hue      = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const THUMB = 36; // avatar size px

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75,
      borderRadius: 1.5, border: `1px solid ${alpha(PRIMARY, 0.13)}`,
      background: '#fafbfc', minWidth: 0,
      transition: 'border-color 0.12s',
      '&:hover': { borderColor: alpha(PRIMARY, 0.3), background: alpha(PRIMARY, 0.03) } }}>
      {/* Square portrait thumbnail */}
      <Box sx={{ width: THUMB, height: THUMB / PORTRAIT_RATIO, borderRadius: 1, overflow: 'hidden',
        flexShrink: 0, background: `hsl(${hue},35%,92%)`, position: 'relative' }}>
        {avatar
          ? <Box component="img" src={avatar} alt={name}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: `hsl(${hue},40%,45%)`, lineHeight: 1 }}>
                {initials}
              </Typography>
            </Box>
        }
      </Box>
      {/* Name + title */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
          {name}
        </Typography>
        {(title || company) && (
          <Typography sx={{ fontSize: '0.65rem', color: '#6b7280',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
            {[title, company].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>
      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
        <Tooltip title="Edit speaker" placement="top">
          <IconButton size="small" onClick={onEdit}
            sx={{ width: 22, height: 22, color: '#b0bac8', '&:hover': { color: PRIMARY, background: alpha(PRIMARY, 0.08) } }}>
            <EditOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove" placement="top">
          <IconButton size="small" onClick={onRemove}
            sx={{ width: 22, height: 22, color: '#b0bac8', '&:hover': { color: '#d32f2f', background: alpha('#d32f2f', 0.08) } }}>
            <CloseOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function SpeakerPickerPopup({ eventSpeakers, selected, onSelect, onClose, onAddCustom }) {
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef();
  React.useEffect(() => { inputRef.current?.focus(); }, []);

  // selected holds full speaker objects; deduplicate by id
  const selectedIds = new Set(selected.map(s => s.id).filter(Boolean));
  const trimmed = query.trim();

  const filtered = eventSpeakers.filter(sp =>
    (sp.name || '').toLowerCase().includes(trimmed.toLowerCase()) && !selectedIds.has(sp.id)
  );

  const showAddNew = trimmed.length > 0 &&
    !eventSpeakers.some(sp => (sp.name || '').toLowerCase() === trimmed.toLowerCase());

  const handleAddNew = () => {
    const newSpeaker = { id: uuid(), name: trimmed };
    onAddCustom(newSpeaker);
    setQuery('');
    onClose();
  };

  return (
    <Box sx={{ display:'flex', flexDirection:'column', height:'100%' }}>
      <Box sx={{ p:1.25, borderBottom:'1px solid #f0f4f8', flexShrink:0 }}>
        <TextField inputRef={inputRef} value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && trimmed) handleAddNew(); if (e.key === 'Escape') onClose(); }}
          fullWidth size="small" placeholder="Search or type a name to add…"
          sx={{ '& .MuiOutlinedInput-root':{ borderRadius:1.5 } }}
          InputProps={{ endAdornment: trimmed ? (
            <Button size="small" variant="contained" onMouseDown={e => { e.preventDefault(); handleAddNew(); }}
              sx={{ borderRadius:1, height:24, fontSize:'0.72rem', px:1, background:PRIMARY, '&:hover':{background:'#0d5fa8'}, flexShrink:0, textTransform:'none' }}>
              Add
            </Button>
          ) : null }}
        />
        {showAddNew && trimmed && (
          <Box onMouseDown={e => { e.preventDefault(); handleAddNew(); }}
            sx={{ mt:0.75, px:1.25, py:0.75, borderRadius:1.5, border:`1px dashed ${alpha(PRIMARY,0.3)}`,
              background:alpha(PRIMARY,0.03), cursor:'pointer', display:'flex', alignItems:'center', gap:1,
              '&:hover':{ background:alpha(PRIMARY,0.08) } }}>
            <PersonAddOutlined sx={{ fontSize:14, color:PRIMARY }} />
            <Typography sx={{ fontSize:'0.8rem', color:PRIMARY, fontWeight:600 }}>Add "{trimmed}" as new speaker</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ flex:1, overflowY:'auto', p:1.5 }}>
        {filtered.length === 0 && !showAddNew && (
          <Typography sx={{ px:1, py:1, fontSize:'0.8rem', color:'text.secondary' }}>
            {eventSpeakers.length === 0 ? 'No speakers added to this event yet.' : trimmed ? 'No matching speakers.' : 'All speakers already added.'}
          </Typography>
        )}
        <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1 }}>
          {filtered.map(sp => {
            const hue = [...(sp.name||'')].reduce((a,c)=>a+c.charCodeAt(0),0)%360;
            const initials = (sp.name||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
            return (
              <Box key={sp.id} onMouseDown={e => { e.preventDefault(); onSelect(sp); onClose(); }}
                sx={{ display:'flex', alignItems:'center', gap:1, p:1, borderRadius:1.5, cursor:'pointer',
                  border:`1px solid ${alpha(PRIMARY,0.1)}`, background:'#fafbfc',
                  '&:hover':{ background:alpha(PRIMARY,0.06), borderColor:alpha(PRIMARY,0.25) }, transition:'all 0.12s' }}>
                {sp.avatar
                  ? <Box component="img" src={sp.avatar} alt={sp.name} sx={{ width:30, height:30, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} />
                  : <Avatar sx={{ width:30, height:30, fontSize:'0.65rem', fontWeight:700, bgcolor:`hsl(${hue},55%,85%)`, color:`hsl(${hue},40%,30%)`, flexShrink:0 }}>{initials}</Avatar>
                }
                <Box sx={{ minWidth:0 }}>
                  <Typography sx={{ fontSize:'0.78rem', fontWeight:600, color:'#1a1a2e', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sp.name}</Typography>
                  {sp.title && <Typography sx={{ fontSize:'0.65rem', color:'text.secondary', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sp.title}</Typography>}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function SpeakerSelector({ selected, eventSpeakers, onChange }) {
  const [pickerOpen, setPickerOpen]   = React.useState(false);
  const [anchorPos, setAnchorPos]     = React.useState(null);
  const [editorOpen, setEditorOpen]   = React.useState(false);
  const [editorSpeaker, setEditorSpeaker] = React.useState(null); // speaker being edited
  const [editorIdx, setEditorIdx]     = React.useState(null);     // index in selected[]
  const btnRef = React.useRef();

  React.useEffect(() => {
    if (!pickerOpen) return;
    const handler = e => {
      const popup = document.getElementById('speaker-picker-popup');
      if (popup && popup.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const openPicker = () => {
    if (pickerOpen) { setPickerOpen(false); return; }
    if (btnRef.current) {
      const rect   = btnRef.current.getBoundingClientRect();
      const drawer = btnRef.current.closest('[role="presentation"] .MuiPaper-root')
                  || btnRef.current.closest('.MuiDrawer-paper')
                  || btnRef.current.closest('[class*="MuiPaper"]');
      const dRect  = drawer ? drawer.getBoundingClientRect() : rect;
      setAnchorPos({ bottom: window.innerHeight - rect.top + 6, left: dRect.left, width: dRect.width });
    }
    setPickerOpen(true);
  };

  const handleSelect    = (speaker) => { onChange([...selected, speaker]); setPickerOpen(false); };
  const handleAddCustom = (speaker) => { onChange([...selected, speaker]); setPickerOpen(false); };
  const handleRemove    = (idx) => { onChange(selected.filter((_, i) => i !== idx)); };

  const openEditor = (speaker, idx) => {
    setEditorSpeaker(speaker);
    setEditorIdx(idx);
    setEditorOpen(true);
  };

  const handleEditorSave = (form) => {
    const next = selected.map((s, i) => i === editorIdx ? { ...s, ...form } : s);
    onChange(next);
    setEditorOpen(false);
    setEditorSpeaker(null);
    setEditorIdx(null);
  };

  return (
    <Box>
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
        <Typography sx={{ fontSize:'0.75rem', fontWeight:600, color:'#374151' }}>Speakers</Typography>
        <Button ref={btnRef} size="small" variant="outlined" startIcon={<PersonAddOutlined sx={{ fontSize:14 }} />}
          onClick={openPicker} sx={{ borderRadius:2, textTransform:'none', fontSize:'0.75rem', height:28, px:1.25 }}>
          Add Speaker
        </Button>
      </Box>

      {selected.length > 0 && (
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
          {selected.map((s, i) => (
            <SpeakerCard key={s.id || i} speaker={s}
              onRemove={() => handleRemove(i)}
              onEdit={() => openEditor(s, i)} />
          ))}
        </Grid>
      )}

      {pickerOpen && anchorPos && (
        <Box id="speaker-picker-popup" sx={{ position:'fixed', bottom:anchorPos.bottom, left:anchorPos.left+16,
          width:anchorPos.width-32, height:340, zIndex:1500, background:'#fff', border:'1px solid #e4e9f0',
          borderRadius:2, boxShadow:'0 -4px 32px rgba(0,0,0,0.15)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <SpeakerPickerPopup eventSpeakers={eventSpeakers} selected={selected}
            onSelect={handleSelect} onAddCustom={handleAddCustom} onClose={() => setPickerOpen(false)} />
        </Box>
      )}

      <SpeakerEditorDrawer
        open={editorOpen}
        speaker={editorSpeaker}
        onClose={() => { setEditorOpen(false); setEditorSpeaker(null); setEditorIdx(null); }}
        onSave={handleEditorSave}
      />
    </Box>
  );
}

// ── Inline quick card ─────────────────────────────────────────────────────────
function QuickCard({ iso, slotIdx, onCommit, onCancel, rowH = ROW_H_DEFAULT }) {
  const [title, setTitle] = useState('');
  const ref = useRef();
  useEffect(() => { ref.current?.focus(); }, []);

  const commit = () => { if (title.trim()) onCommit(title.trim()); else onCancel(); };
  const startTime = slotToTime(slotIdx);
  const endTime   = slotToTime(Math.min(slotIdx + 4, TIME_SLOTS.length * 2 - 1));
  const m         = TYPE_META.session;

  return (
    <Box sx={{ position:'absolute', top:`${snapSlotToPct(slotIdx)}%`, left:3, right:3,
      height:`${100/TOTAL_SNAPS * 4}%`, background:m.bg, borderRadius:1.5,
      border:`1.5px solid ${alpha(m.color,0.4)}`, borderLeft:`3px solid ${m.color}`,
      px:0.75, py:0.5, zIndex:10, display:'flex', flexDirection:'column', gap:0.5 }}>
      <Typography sx={{ fontSize:'0.6rem', color:alpha(m.color,0.7), fontWeight:600 }}>
        {startTime} → {endTime}
      </Typography>
      <input ref={ref} value={title} onChange={e=>setTitle(e.target.value)}
        onKeyDown={e=>{ if(e.key==='Enter') commit(); if(e.key==='Escape') onCancel(); }}
        onBlur={commit} placeholder="Session title…"
        style={{ border:'none', background:'transparent', outline:'none',
          fontSize:'0.75rem', fontWeight:700, color:m.color, width:'100%', padding:0, fontFamily:'inherit' }} />
    </Box>
  );
}


// ── Card action buttons ───────────────────────────────────────────────────────
function CardActions({ m, onDelete, onDuplicate, session }) {
  const [visible, setVisible] = React.useState(false);
  return (
    <Box onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}
      onPointerDown={e => e.stopPropagation()}
      sx={{ position:'absolute', top:0, right:0, bottom:0, left:0, zIndex:5, pointerEvents:'none' }}>
      <Box sx={{ position:'absolute', top:4, right:4, display:'flex', gap:0.5,
        opacity:visible?1:0, transition:'opacity 0.15s', pointerEvents:visible?'auto':'none' }}>
        <Box onClick={e => { e.stopPropagation(); onDuplicate?.(session); }} onPointerDown={e => e.stopPropagation()}
          sx={{ width:22, height:22, borderRadius:1, background:alpha(m.color,0.18),
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
            '&:hover':{ background:alpha(m.color,0.35) } }}>
          <ContentCopyOutlined sx={{ fontSize:13, color:m.color }} />
        </Box>
        <Box onClick={e => { e.stopPropagation(); onDelete?.(session.id); }} onPointerDown={e => e.stopPropagation()}
          sx={{ width:22, height:22, borderRadius:1, background:alpha('#d32f2f',0.15),
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
            '&:hover':{ background:alpha('#d32f2f',0.3) } }}>
          <DeleteOutlined sx={{ fontSize:13, color:'#d32f2f' }} />
        </Box>
      </Box>
    </Box>
  );
}


// ── Card context menu ─────────────────────────────────────────────────────────
function CardContextMenu({ x, y, onDuplicate, onDelete, onClose }) {
  React.useEffect(() => {
    const handler = () => onClose();
    document.addEventListener('click', handler);
    document.addEventListener('contextmenu', handler);
    return () => { document.removeEventListener('click', handler); document.removeEventListener('contextmenu', handler); };
  }, [onClose]);
  return (
    <Box sx={{ position:'fixed', top:y, left:x, zIndex:9999, background:'#fff', border:'1px solid #e4e9f0',
      borderRadius:2, boxShadow:'0 8px 24px rgba(0,0,0,0.15)', overflow:'hidden', minWidth:140 }}>
      <Box onClick={e => { e.stopPropagation(); onDuplicate(); onClose(); }}
        sx={{ px:2, py:1.25, cursor:'pointer', display:'flex', alignItems:'center', gap:1.25, '&:hover':{ background:alpha(PRIMARY,0.06) } }}>
        <ContentCopyOutlined sx={{ fontSize:15, color:'#5a6474' }} />
        <Typography sx={{ fontSize:'0.875rem', color:'#374151' }}>Duplicate</Typography>
      </Box>
      <Box sx={{ height:1, background:'#f0f2f5' }} />
      <Box onClick={e => { e.stopPropagation(); onDelete(); onClose(); }}
        sx={{ px:2, py:1.25, cursor:'pointer', display:'flex', alignItems:'center', gap:1.25, '&:hover':{ background:alpha('#d32f2f',0.06) } }}>
        <DeleteOutlined sx={{ fontSize:15, color:'#d32f2f' }} />
        <Typography sx={{ fontSize:'0.875rem', color:'#d32f2f' }}>Delete</Typography>
      </Box>
    </Box>
  );
}


// ── Speaker display helpers ───────────────────────────────────────────────────
function SpeakerAvatars({ speakers, max = 3, size = 16, color }) {
  if (!speakers || speakers.length === 0) return null;
  const visible = speakers.slice(0, max);
  return (
    <Box sx={{ display:'flex', alignItems:'center' }}>
      {visible.map((s, i) => {
        const name    = typeof s === 'string' ? s : s.name || '';
        const avatar  = typeof s === 'object' ? s.avatar : null;
        const hue     = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
        return avatar ? (
          <Box key={i} component="img" src={avatar} alt={name}
            sx={{ width:size, height:size, borderRadius:'50%', objectFit:'cover', ml:i>0?-0.5:0, border:'1.5px solid #fff', flexShrink:0 }} />
        ) : (
          <Box key={i} sx={{ width:size, height:size, borderRadius:'50%', flexShrink:0, ml:i>0?-0.5:0, border:'1.5px solid #fff',
            background:`hsl(${hue},55%,85%)`, display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:size*0.4, fontWeight:700, color:`hsl(${hue},40%,30%)` }}>
            {name[0]?.toUpperCase()}
          </Box>
        );
      })}
      {speakers.length > max && (
        <Box sx={{ width:size, height:size, borderRadius:'50%', flexShrink:0, ml:-0.5, border:'1.5px solid #fff',
          background:alpha(color||'#666',0.15), display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:size*0.38, fontWeight:700, color:color||'#666' }}>
          +{speakers.length - max}
        </Box>
      )}
    </Box>
  );
}

function SpeakerNameList({ speakers, color }) {
  if (!speakers || speakers.length === 0) return null;
  const names = speakers.map(s => typeof s === 'string' ? s : s.name || '').filter(Boolean);
  return (
    <Box sx={{ display:'flex', alignItems:'center', gap:0.5, mt:0.3 }}>
      <RecordVoiceOverOutlined sx={{ fontSize:9, color:alpha(color,0.6), flexShrink:0 }} />
      <Typography sx={{ fontSize:'0.62rem', color:alpha(color,0.75), overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {names.join(', ')}
      </Typography>
    </Box>
  );
}


// ── Draggable session card ────────────────────────────────────────────────────
function DraggableCard({ session, colIndex, calendarDays, colWidth, onUpdate, onEdit, onDelete, onDuplicate, rowH = ROW_H, col = 0, totalCols = 1 }) {
  const COL_W     = colWidth;
  const m         = TYPE_META[session.type] || TYPE_META.session;
  const cardRef   = useRef();
  const dragState = useRef(null);
  const [ctxMenu, setCtxMenu] = React.useState(null);

  const startSlot = timeToSlot(session.timeSlots?.[0]?.startTime || session.startTime) ?? 0;
  const endSlot   = timeToSlot(session.timeSlots?.[0]?.endTime   || session.endTime)   ?? (startSlot + 2);
  const topPct    = snapSlotToPct(startSlot);
  const heightPct = snapSlotToPct(endSlot) - topPct;

  const _st = session.timeSlots?.[0]?.startTime || session.startTime || '';
  const _et = session.timeSlots?.[0]?.endTime   || session.endTime   || '';
  const tDisplay = _st ? `${fmtTime(_st)}${_et ? ` → ${fmtTime(_et)}` : ''}` : '';

  const onPointerDownBody = (e) => {
    if (e.target.dataset.handle) return;
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = { mode:'move', originY:e.clientY, originX:e.clientX, startSlot, endSlot,
      colIndex, draftSlot:startSlot, draftCol:colIndex, duration:endSlot-startSlot, hasMoved:false };
  };
  const onPointerDownTop = (e) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = { mode:'resize-top', originY:e.clientY, startSlot, endSlot, draftStart:startSlot, hasMoved:false };
  };
  const onPointerDownBottom = (e) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = { mode:'resize-bottom', originY:e.clientY, startSlot, endSlot, draftEnd:endSlot, hasMoved:false };
  };

  const onPointerMove = (e) => {
    const ds = dragState.current;
    if (!ds || !cardRef.current) return;
    const card = cardRef.current;
    const dySnaps = Math.round((e.clientY - ds.originY) / (rowH / 2));
    const dxCols  = Math.round((e.clientX - ds.originX) / COL_W);
    if (ds.mode === 'move') {
      const newSlot = Math.max(0, Math.min(ds.startSlot + dySnaps, TIME_SLOTS.length * 2 - ds.duration - 1));
      const newCol  = Math.max(0, Math.min(ds.colIndex  + dxCols, calendarDays.length - 1));
      ds.draftSlot = newSlot; ds.draftCol = newCol; ds.hasMoved = dySnaps !== 0 || dxCols !== 0;
      card.style.top       = `${snapSlotToPct(newSlot)}%`;
      card.style.opacity   = '0.85';
      card.style.boxShadow = `0 8px 24px ${alpha(m.color,0.35)}`;
      card.style.transform = `translateX(${(newCol - ds.colIndex) * COL_W}px) scale(1.02)`;
    } else if (ds.mode === 'resize-top') {
      const newStart = Math.max(0, Math.min(ds.startSlot + dySnaps, ds.endSlot - 2));
      ds.draftStart = newStart; ds.hasMoved = dySnaps !== 0;
      card.style.top    = `${snapSlotToPct(newStart)}%`;
      card.style.height = `${snapSlotToPct(ds.endSlot) - snapSlotToPct(newStart)}%`;
    } else if (ds.mode === 'resize-bottom') {
      const newEnd = Math.max(ds.startSlot + 2, Math.min(ds.endSlot + dySnaps, TIME_SLOTS.length * 2));
      ds.draftEnd = newEnd; ds.hasMoved = dySnaps !== 0;
      card.style.height = `${snapSlotToPct(newEnd) - snapSlotToPct(ds.startSlot)}%`;
    }
  };

  const onPointerUp = (e) => {
    const ds = dragState.current;
    dragState.current = null;
    if (!ds || !cardRef.current) return;
    const card = cardRef.current;
    card.style.transform = ''; card.style.opacity = ''; card.style.boxShadow = '';
    if (!ds.hasMoved) {
      card.style.top = ''; card.style.height = '';
      onEdit(session); return;
    }
    if (ds.mode === 'move') {
      const newIso   = calendarDays[ds.draftCol]?.iso || session.dayLabel;
      const newStart = slotToTime(ds.draftSlot);
      const newEnd   = slotToTime(ds.draftSlot + ds.duration);
      onUpdate({ ...session, dayLabel:newIso, startTime:newStart, endTime:newEnd, timeSlots:[{startTime:newStart,endTime:newEnd}] });
    } else if (ds.mode === 'resize-top') {
      const newStart = slotToTime(ds.draftStart);
      onUpdate({ ...session, startTime:newStart, timeSlots:[{startTime:newStart,endTime:session.timeSlots?.[0]?.endTime||session.endTime}] });
    } else if (ds.mode === 'resize-bottom') {
      const newEnd = slotToTime(ds.draftEnd);
      onUpdate({ ...session, endTime:newEnd, timeSlots:[{startTime:session.timeSlots?.[0]?.startTime||session.startTime,endTime:newEnd}] });
    }
  };

  const HANDLE_H = 6;
  const leftPct  = (col / totalCols) * 100;
  const rightPct = ((totalCols - col - 1) / totalCols) * 100;
  const GAP = 3;

  return (
    <Box ref={cardRef} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x:e.clientX, y:e.clientY }); }}
      sx={{ position:'absolute', top:`${topPct}%`, left:`calc(${leftPct}% + ${GAP}px)`, right:`calc(${rightPct}% + ${GAP}px)`,
        height:`${Math.max(heightPct,100/TOTAL_SNAPS)}%`, background:m.bg, borderRadius:1.5,
        border:`1px solid ${alpha(m.color,0.25)}`, borderLeft:`3px solid ${m.color}`,
        overflow:'visible', zIndex:2, transition:'box-shadow 0.15s', cursor:'grab', userSelect:'none', touchAction:'none' }}>

      <Box data-handle="top" onPointerDown={onPointerDownTop}
        sx={{ position:'absolute', top:0, left:0, right:0, height:HANDLE_H, cursor:'ns-resize', zIndex:4,
          borderRadius:'6px 6px 0 0', '&:hover':{ background:alpha(m.color,0.25) } }} />

      <Box onPointerDown={onPointerDownBody}
        sx={{ position:'absolute', top:HANDLE_H, left:0, right:0, bottom:HANDLE_H, px:0.75, py:0.5,
          overflow:'hidden', cursor:'grab', '&:active':{ cursor:'grabbing' } }}>
        {heightPct < (400/TOTAL_SNAPS) ? (
          <Box sx={{ display:'flex', alignItems:'center', gap:0.5, minWidth:0, width:'100%' }}>
            {session.posterUrl && <Box component="img" src={session.posterUrl} alt=""
              sx={{ width:18, height:18, borderRadius:0.5, objectFit:'cover', flexShrink:0, border:`1px solid ${alpha(m.color,0.2)}` }} />}
            <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:m.color, lineHeight:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0 }}>
              {session.title}
            </Typography>
            {(session.speakers||[]).length > 0 && <Box sx={{ flexShrink:0 }}><SpeakerAvatars speakers={session.speakers} max={2} size={14} color={m.color} /></Box>}
            {tDisplay && <Typography sx={{ fontSize:'0.6rem', color:alpha(m.color,0.7), whiteSpace:'nowrap', flexShrink:0 }}>{tDisplay}</Typography>}
          </Box>
        ) : (
          <Box sx={{ display:'flex', alignItems:'flex-start', gap:0.75, minWidth:0 }}>
            {session.posterUrl && <Box component="img" src={session.posterUrl} alt=""
              sx={{ width:28, height:28, borderRadius:1, objectFit:'cover', flexShrink:0, border:`1px solid ${alpha(m.color,0.2)}` }} />}
            <Box sx={{ minWidth:0, flex:1 }}>
              <Box sx={{ display:'flex', alignItems:'flex-start', gap:0.5, minWidth:0 }}>
                <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:m.color, lineHeight:1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0 }}>
                  {session.title}
                </Typography>
                {tDisplay && <Typography sx={{ fontSize:'0.6rem', color:alpha(m.color,0.7), whiteSpace:'nowrap', flexShrink:0, lineHeight:1.4 }}>{tDisplay}</Typography>}
              </Box>
              {(session.speakers||[]).length > 0 && <SpeakerNameList speakers={session.speakers} color={m.color} />}
              {session.location && (
                <Box sx={{ display:'flex', alignItems:'center', gap:0.3, mt:0.25 }}>
                  <LocationOnOutlined sx={{ fontSize:9, color:alpha(m.color,0.6) }} />
                  <Typography sx={{ fontSize:'0.6rem', color:alpha(m.color,0.7), overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {session.location}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
        <CardActions m={m} onDelete={onDelete} onDuplicate={onDuplicate} session={session} />
      </Box>

      {ctxMenu && (
        <CardContextMenu x={ctxMenu.x} y={ctxMenu.y}
          onDuplicate={() => onDuplicate?.(session)} onDelete={() => onDelete?.(session.id)}
          onClose={() => setCtxMenu(null)} />
      )}

      <Box data-handle="bottom" onPointerDown={onPointerDownBottom}
        sx={{ position:'absolute', bottom:0, left:0, right:0, height:HANDLE_H, cursor:'ns-resize', zIndex:4,
          borderRadius:'0 0 6px 6px', '&:hover':{ background:alpha(m.color,0.25) },
          display:'flex', alignItems:'center', justifyContent:'center' }}>
        <Box sx={{ width:20, height:2, borderRadius:1, background:alpha(m.color,0.35) }} />
      </Box>
    </Box>
  );
}


// ── Side-by-side overlap detection ───────────────────────────────────────────
function assignColumns(sessions) {
  const sorted = [...sessions].sort((a, b) => {
    const aStart = timeToSlot(a.timeSlots?.[0]?.startTime || a.startTime) ?? 0;
    const bStart = timeToSlot(b.timeSlots?.[0]?.startTime || b.startTime) ?? 0;
    return aStart !== bStart ? aStart - bStart : (a.id||'').localeCompare(b.id||'');
  });
  const colEnds = [];
  const colAssign = sorted.map(s => {
    const startSlot = timeToSlot(s.timeSlots?.[0]?.startTime || s.startTime) ?? 0;
    const endSlot   = timeToSlot(s.timeSlots?.[0]?.endTime   || s.endTime)   ?? (startSlot + 4);
    let col = 0;
    while (col < colEnds.length && colEnds[col] > startSlot) col++;
    colEnds[col] = endSlot;
    return { ...s, _col: col };
  });
  const withTotals = colAssign.map(s => {
    const sStart = timeToSlot(s.timeSlots?.[0]?.startTime || s.startTime) ?? 0;
    const sEnd   = timeToSlot(s.timeSlots?.[0]?.endTime   || s.endTime)   ?? (sStart + 4);
    let maxCol = s._col;
    colAssign.forEach(other => {
      const oStart = timeToSlot(other.timeSlots?.[0]?.startTime || other.startTime) ?? 0;
      const oEnd   = timeToSlot(other.timeSlots?.[0]?.endTime   || other.endTime)   ?? (oStart + 4);
      if (oStart < sEnd && oEnd > sStart) maxCol = Math.max(maxCol, other._col);
    });
    return { ...s, _totalCols: maxCol + 1 };
  });
  const map = Object.fromEntries(withTotals.map(s => [s.id, { _col: s._col, _totalCols: s._totalCols }]));
  return sessions.map(s => ({ ...s, ...(map[s.id] || { _col:0, _totalCols:1 }) }));
}


// ── Calendar grid ─────────────────────────────────────────────────────────────
function CalendarGrid({ schedule, calendarDays, onSessionAdd, onSessionClick, onSessionUpdate, onSessionDelete, onSessionDuplicate, rowH, onRowHChange, scrollRef }) {
  const outerRef   = useRef();
  const hScrollRef = useRef();
  const vScrollRef = useRef();
  const today      = isoDate(new Date());
  const [quickCard, setQuickCard]   = useState(null);
  const [containerW, setContainerW] = useState(0);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const measure = () => setContainerW(el.clientWidth - TIME_W);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    requestAnimationFrame(measure);
    return () => ro.disconnect();
  }, []);

  const ROW_H = rowH ?? ROW_H_DEFAULT;
  const eventDayCols = Math.max(calendarDays.filter(d => d.isEventDay).length, 1);
  const COL_W = containerW > 0 ? Math.max(COL_W_MIN, Math.floor(containerW / eventDayCols)) : COL_W_MIN;

  const eventDayNumberMap = useMemo(() => {
    let n = 0; const map = {};
    calendarDays.forEach(d => { if (d.isEventDay) { n++; map[d.iso] = n; } });
    return map;
  }, [calendarDays]);

  const sessionMap = useMemo(() => {
    const m = {};
    schedule.forEach(s => { if (!s.dayLabel) return; if (!m[s.dayLabel]) m[s.dayLabel] = []; m[s.dayLabel].push(s); });
    return m;
  }, [schedule]);

  const doCenter = useCallback(() => {
    const el = hScrollRef.current;
    if (!el) return;
    const eventDays = calendarDays.filter(d => d.isEventDay);
    const idx = calendarDays.findIndex(d => d.isEventDay);
    if (idx >= 0) {
      const viewW = el.clientWidth;
      const colW  = Math.max(148, Math.floor(viewW / Math.max(eventDays.length, 1)));
      el.scrollLeft = Math.max(0, (idx) * colW + 10);
    }
  }, [COL_W, containerW, calendarDays]);

  const centeredAtColW = useRef(0);
  useEffect(() => {
    if (containerW === 0 || COL_W === COL_W_MIN) return;
    if (Math.abs(COL_W - centeredAtColW.current) < 1) return;
    centeredAtColW.current = COL_W;
    requestAnimationFrame(doCenter);
  }, [COL_W, containerW, doCenter]);

  const commitQuickCard = (title) => {
    if (!quickCard) return;
    const startTime = slotToTime(quickCard.slotIdx);
    const endTime   = slotToTime(Math.min(quickCard.slotIdx + 4, TIME_SLOTS.length * 2 - 1));
    onSessionAdd({ id:uuid(), title, dayLabel:quickCard.iso, startTime, endTime,
      timeSlots:[{startTime,endTime}], type:'session', speakers:[], location:'' });
    setQuickCard(null);
  };

  const totalW = calendarDays.length * COL_W;

  return (
    <Box ref={outerRef} sx={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden', position:'relative' }}>
      {/* ── Sticky header ── */}
      <Box sx={{ display:'flex', flexShrink:0, borderBottom:'2px solid #e4e9f0', zIndex:20, background:'#fff' }}>
        <Box sx={{ width:TIME_W, flexShrink:0, borderRight:'1px solid #e4e9f0', background:'#fafbfc' }} />
        <Box sx={{ flex:1, overflow:'hidden' }}>
          <Box sx={{ display:'flex', transform:'translateX(0)', width:totalW }} id="cal-header-inner">
            {calendarDays.map((day, di) => {
              const isToday = day.iso === today;
              const isEvent = day.isEventDay;
              return (
                <Box key={day.iso} sx={{ width:COL_W, flexShrink:0, height:HEADER_H, display:'flex', flexDirection:'column',
                  alignItems:'center', justifyContent:'center', gap:0.25,
                  borderRight:di<calendarDays.length-1?'1px solid #e4e9f0':'none',
                  background:isEvent?alpha(PRIMARY,0.05):'#f5f6f8' }}>
                  {(() => {
                    const d = new Date(day.iso + 'T00:00:00');
                    return (
                      <Typography sx={{ fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em',
                        color:isToday?PRIMARY:isEvent?'#6b7280':'#bcc4ce', lineHeight:1 }}>
                        {MONTH_NAMES[d.getMonth()]} {d.getFullYear()}
                      </Typography>
                    );
                  })()}
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.75 }}>
                    <Box sx={{ width:32, height:32, borderRadius:'50%', background:isToday?PRIMARY:'transparent',
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Typography sx={{ fontSize:'1rem', fontWeight:isToday?800:isEvent?700:400,
                        color:isToday?'#fff':isEvent?PRIMARY:'#c0c8d4', lineHeight:1 }}>
                        {day.date}
                      </Typography>
                    </Box>
                    {isEvent && eventDayNumberMap[day.iso] && (
                      <Typography sx={{ fontSize:'1rem', fontWeight:700, color:PRIMARY, lineHeight:1, whiteSpace:'nowrap' }}>
                        Day {eventDayNumberMap[day.iso]}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex:1, display:'flex', overflow:'hidden' }}>
        <Box sx={{ width:TIME_W, flexShrink:0, borderRight:'1px solid #e4e9f0', zIndex:15, background:'#fff', overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <Box id="cal-gutter-inner" sx={{ flex:1, overflowY:'hidden' }}>
            {TIME_SLOTS.map((slot, i) => (
              <Box key={i} sx={{ height:ROW_H, flexShrink:0, position:'relative',
                borderBottom:i<TIME_SLOTS.length-1?'1px solid #f0f2f5':0,
                background:slot.isNoon?alpha('#64748b',0.05):'#fff',
                display:'flex', alignItems:'flex-start', justifyContent:'flex-end', pr:1.25, pt:0.5 }}>
                <Typography sx={{ fontSize:'0.62rem', fontWeight:slot.isNoon?800:600,
                  color:slot.isNoon?'#475569':'#9ea8b3', whiteSpace:'nowrap' }}>
                  {slot.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box ref={el => { vScrollRef.current=el; hScrollRef.current=el; if(scrollRef) scrollRef.current=el; }}
          onScroll={e => {
            const hdr = document.getElementById('cal-header-inner');
            if (hdr) hdr.style.transform = `translateX(-${e.target.scrollLeft}px)`;
            const gut = document.getElementById('cal-gutter-inner');
            if (gut) gut.scrollTop = e.target.scrollTop;
          }}
          sx={{ flex:1, overflowX:'auto', overflowY:'auto',
            '&::-webkit-scrollbar':{ height:8, width:6 },
            '&::-webkit-scrollbar-thumb':{ background:'#d0d5dd', borderRadius:4 },
            '&::-webkit-scrollbar-corner':{ background:'transparent' } }}>
          <Box sx={{ display:'flex', width:totalW }}>
            {calendarDays.map((day, di) => {
              const sessions = assignColumns(sessionMap[day.iso] || []);
              const isEvent  = day.isEventDay;
              const hasQuick = quickCard?.iso === day.iso;
              return (
                <Box key={day.iso} sx={{ width:COL_W, flexShrink:0, borderRight:di<calendarDays.length-1?'1px solid #e4e9f0':'none', position:'relative', display:'flex', flexDirection:'column' }}>
                  {TIME_SLOTS.map((slot, i) => {
                    const isNoon = slot.isNoon;
                    return (
                      <Box key={i} sx={{ position:'relative', height:ROW_H, flexShrink:0 }}>
                        <Box onClick={() => { if(!isEvent) return; setQuickCard({ iso:day.iso, slotIdx:i*2 }); }}
                          sx={{ height:'50%', position:'relative', borderTop:i===0?0:'1px solid #f0f2f5',
                            background:!isEvent?(i%2===0?'#f5f6f8':'#f0f1f3'):isNoon?alpha('#64748b',0.06):'transparent',
                            cursor:isEvent?'pointer':'default',
                            '&:hover':isEvent?{ background:isNoon?alpha('#64748b',0.1):alpha(PRIMARY,0.06) }:{}, transition:'background 0.1s' }} />
                        <Box onClick={() => { if(!isEvent) return; setQuickCard({ iso:day.iso, slotIdx:i*2+1 }); }}
                          sx={{ height:'50%', borderTop:'1px dashed #f0f2f5',
                            background:!isEvent?(i%2===0?'#f5f6f8':'#f0f1f3'):isNoon?alpha('#64748b',0.06):'transparent',
                            cursor:isEvent?'pointer':'default',
                            '&:hover':isEvent?{ background:isNoon?alpha('#64748b',0.1):alpha(PRIMARY,0.06) }:{}, transition:'background 0.1s' }} />
                      </Box>
                    );
                  })}
                  {hasQuick && quickCard && (
                    <QuickCard iso={day.iso} slotIdx={quickCard.slotIdx}
                      onCommit={commitQuickCard} onCancel={() => setQuickCard(null)} rowH={ROW_H} />
                  )}
                  {sessions.map(s => (
                    <DraggableCard key={s.id} session={s} colIndex={di} calendarDays={calendarDays}
                      colWidth={COL_W} onUpdate={onSessionUpdate} onEdit={onSessionClick}
                      onDelete={onSessionDelete} onDuplicate={onSessionDuplicate}
                      rowH={ROW_H} col={s._col||0} totalCols={s._totalCols||1} />
                  ))}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}


// ── Main ──────────────────────────────────────────────────────────────────────
const EventScheduleTab = forwardRef(function EventScheduleTab({ event, onSave }, ref) {
  const [schedule, setSchedule]     = useState([]);
  const [editSession, setEditSession] = useState(null);
  const [rowH, setRowH]             = useState(ROW_H_MIN);
  const calendarScrollRef           = useRef();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');
  const [saved, setSaved]           = useState(false);
  const [ready, setReady]           = useState(false);

  const autoSaveTimer  = useRef(null);
  const serverSnapshot = useRef(null);

  useEffect(() => {
    // API returns schedule with speakers already resolved to full objects
    // (via resolveScheduleSpeakers on the backend). Keep them as objects in
    // local state for display; serializeSchedule collapses back to IDs on save.
    const loaded = event?.schedule ? event.schedule.map(s => ({ ...s, id: s.id || uuid() })) : [];
    serverSnapshot.current = JSON.stringify(loaded);
    setSchedule(loaded);
    setReady(true);
  }, [event]);

  useEffect(() => {
    const timer = setTimeout(() => handleRecenter(), 1000);
    return () => clearTimeout(timer);
  }, []);

  const calendarDays = useMemo(() => {
    const eventStartIso = event?.startDate
      ? (typeof event.startDate === 'string' ? event.startDate.split('T')[0] : isoDate(new Date(event.startDate)))
      : isoDate(new Date());
    const eventEndIso = event?.endDate
      ? (typeof event.endDate === 'string' ? event.endDate.split('T')[0] : isoDate(new Date(event.endDate)))
      : eventStartIso;
    const startAnchor = parseLocalDate(eventStartIso);
    const endAnchor   = parseLocalDate(eventEndIso);
    const rangeStart  = new Date(startAnchor.getFullYear(), startAnchor.getMonth(), startAnchor.getDate()-7);
    const rangeEnd    = new Date(endAnchor.getFullYear(),   endAnchor.getMonth(),   endAnchor.getDate()+7);
    const total       = Math.round((rangeEnd-rangeStart)/86400000)+1;
    return Array.from({length:total},(_,i)=>{
      const d   = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()+i);
      const iso = isoDate(d);
      return { iso, weekday:WEEKDAY_SHORT[d.getDay()], date:d.getDate(),
        monthLabel:(i===0||d.getDate()===1)?MONTH_NAMES[d.getMonth()]:'',
        isEventDay: iso>=eventStartIso && iso<=eventEndIso };
    });
  }, [event]);

  const handleSessionDelete    = useCallback((id) => setSchedule(prev => prev.filter(s => s.id !== id)), []);
  const handleSessionDuplicate = useCallback((session) => {
    setSchedule(prev => [...prev, { ...session, id: uuid(), title: `${session.title} (copy)` }]);
  }, []);
  const handleSessionUpdate    = useCallback((updated) => setSchedule(prev => prev.map(s => s.id === updated.id ? updated : s)), []);
  const handleSessionAdd       = useCallback((session) => setSchedule(prev => [...prev, session]), []);
  const handleSessionClick     = (s) => { setEditSession(s); setDrawerOpen(true); };

  const handleDrawerSave = useCallback(async (form) => {
    const sessionId = editSession?.id || uuid();
    const merged = {
      ...form,
      id: sessionId,
      startTime: form.startTime || '',
      endTime:   form.endTime   || '',
      timeSlots: [{ startTime: form.startTime || '', endTime: form.endTime || '' }],
      speakers: form.speakers || [],  // full objects in local state; collapsed to IDs for API
    };

    // Merge session speakers back into event.speakers:
    //  - existing speaker (same id) → replace with updated version
    //  - new speaker (id not found)  → append
    const sessionSpeakers = form.speakers || [];
    const sessionSpeakerMap = Object.fromEntries(sessionSpeakers.map(s => [s.id, s]));
    const base = event.speakers || [];
    const merged_speakers = [
      // Update any existing speakers that were edited
      ...base.map(s => sessionSpeakerMap[s.id] ? { ...s, ...sessionSpeakerMap[s.id] } : s),
      // Append brand-new speakers not yet in event.speakers
      ...sessionSpeakers.filter(s => s.id && !base.some(b => b.id === s.id)),
    ];
    const speakersChanged = JSON.stringify(merged_speakers) !== JSON.stringify(base);

    // Build updated schedule with speaker objects replaced by IDs
    const updatedSchedule = (() => {
      const prev = schedule;
      return editSession
        ? prev.map(s => s.id === editSession.id ? merged : s)
        : [...prev, merged];
    })();

    // Single PUT: always send schedule (IDs); send speakers only if something changed
    const payload = {
      schedule: serializeSchedule(updatedSchedule),
      ...(speakersChanged && { speakers: merged_speakers }),
    };

    try {
      await api.put(`/events/${event._id}`, payload);
      setSchedule(updatedSchedule);
      serverSnapshot.current = JSON.stringify(updatedSchedule);
      onSave?.({
        schedule: updatedSchedule,
        ...(speakersChanged && { speakers: merged_speakers }),
      });
    } catch (e) {
      console.warn('Failed to save session:', e);
    }

    setDrawerOpen(false);
  }, [editSession, event, schedule, onSave]);

  const handleDrawerDelete = () => {
    if (editSession) setSchedule(prev => prev.filter(s => s.id !== editSession.id));
    setDrawerOpen(false);
  };

  const handleRecenter = () => {
    const el = calendarScrollRef.current;
    if (!el) return;
    const eventDays = calendarDays.filter(d => d.isEventDay);
    const idx = calendarDays.findIndex(d => d.isEventDay);
    if (idx >= 0) {
      const viewW = el.clientWidth;
      const colW  = Math.max(148, Math.floor(viewW / Math.max(eventDays.length, 1)));
      el.scrollLeft = Math.max(0, (idx) * colW + 10);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true); setSaveError('');
    try {
      // Collapse speaker objects → IDs and strip UI-only fields before sending
      const payload = serializeSchedule(schedule);
      await api.put(`/events/${event._id}`, { schedule: payload });
      serverSnapshot.current = JSON.stringify(schedule);
      setSaved(true);
      onSave?.({ schedule });
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setSaveError(e?.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  // Auto-save: debounce 1s after any schedule change.
  useEffect(() => {
    if (serverSnapshot.current === null) return;
    if (JSON.stringify(schedule) === serverSnapshot.current) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => handleSaveAll(), 1000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [schedule]); // eslint-disable-line

  useImperativeHandle(ref, () => ({
    save: handleSaveAll,
    get saving() { return saving; },
    get saved()  { return saved; },
  }), [handleSaveAll, saving, saved]);

  if (!ready) return (
    <Box sx={{ display:'flex', flexDirection:'column', flex:1, minHeight:0, overflow:'hidden' }}>
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, flexShrink:0 }}>
        <Box><Skeleton width={160} height={28} sx={{ mb:0.5 }} /><Skeleton width={100} height={18} /></Box>
        <Box sx={{ display:'flex', gap:1 }}>
          <Skeleton variant="rectangular" width={90}  height={32} sx={{ borderRadius:2 }} />
          <Skeleton variant="rectangular" width={110} height={32} sx={{ borderRadius:2 }} />
        </Box>
      </Box>
      <Box sx={{ display:'flex', gap:1, mb:2, flexShrink:0 }}>
        {[...Array(7)].map((_, i) => <Skeleton key={i} variant="rectangular" width={64} height={56} sx={{ borderRadius:2 }} />)}
      </Box>
      <Box sx={{ flex:1 }}>
        {[...Array(5)].map((_, i) => (
          <Box key={i} sx={{ display:'flex', alignItems:'center', gap:2, mb:1.5 }}>
            <Skeleton width={52} height={18} />
            <Skeleton variant="rectangular" sx={{ flex:1, height:52, borderRadius:2 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display:'flex', flexDirection:'column', flex:1, minHeight:0, overflow:'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, flexWrap:'wrap', gap:1, flexShrink:0 }}>
        <Box>
          <Typography sx={{ fontWeight:800, fontSize:'1.05rem', color:'#1a1a2e' }}>Event Schedule</Typography>
          <Typography variant="caption" color="text.secondary">
            Click a cell to add a session · {schedule.length} session{schedule.length!==1?'s':''}
          </Typography>
        </Box>
        <Box sx={{ display:'flex', gap:1 }}>
          <Button size="small" variant="outlined" startIcon={<AddOutlined />}
            onClick={() => { setEditSession(null); setDrawerOpen(true); }}
            sx={{ borderRadius:2, textTransform:'none' }}>Add Session</Button>
        </Box>
      </Box>

      {/* Calendar */}
      <Box sx={{ flex:1, minHeight:0, border:'1px solid #e4e9f0', borderRadius:2.5, overflow:'hidden',
        display:'flex', flexDirection:'column', position:'relative' }}>
        {/* Floating controls */}
        <Box sx={{ position:'absolute', bottom:16, right:16, zIndex:30, display:'flex', alignItems:'center', gap:1, pointerEvents:'none' }}>
          <Box onClick={handleRecenter} sx={{ pointerEvents:'auto', display:'flex', alignItems:'center', gap:0.6,
            px:1.5, py:0.75, borderRadius:3, background:'#fff', boxShadow:'0 2px 12px rgba(0,0,0,0.15)',
            border:`1px solid ${alpha(PRIMARY,0.2)}`, cursor:'pointer', userSelect:'none', transition:'all 0.15s',
            '&:hover':{ background:alpha(PRIMARY,0.06), boxShadow:'0 4px 16px rgba(0,0,0,0.18)' } }}>
            <MyLocationOutlined sx={{ fontSize:16, color:PRIMARY }} />
            <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:PRIMARY, lineHeight:1 }}>Recenter</Typography>
          </Box>
          <Box sx={{ pointerEvents:'auto', display:'flex', alignItems:'center', gap:0, borderRadius:3,
            background:'#fff', boxShadow:'0 2px 12px rgba(0,0,0,0.15)', border:'1px solid #e4e9f0', overflow:'hidden' }}>
            <Box onClick={() => setRowH(h => Math.max(ROW_H_MIN, h - 16))}
              sx={{ px:1.25, py:0.75, cursor:'pointer', display:'flex', alignItems:'center', borderRight:'1px solid #f0f2f5', '&:hover':{ background:'#f5f7fa' } }}>
              <RemoveCircleOutlined sx={{ fontSize:16, color:'#5a6474' }} />
            </Box>
            <Box sx={{ px:1.25, minWidth:46, textAlign:'center' }}>
              <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#374151' }}>{Math.round(100 + ((rowH - ROW_H_MIN) / (200 - ROW_H_MIN)) * 100)}%</Typography>
            </Box>
            <Box onClick={() => setRowH(h => Math.min(200, h + 16))}
              sx={{ px:1.25, py:0.75, cursor:'pointer', display:'flex', alignItems:'center', borderLeft:'1px solid #f0f2f5', '&:hover':{ background:'#f5f7fa' } }}>
              <AddCircleOutlined sx={{ fontSize:16, color:'#5a6474' }} />
            </Box>
          </Box>
        </Box>

        <CalendarGrid
          schedule={schedule} calendarDays={calendarDays}
          onSessionAdd={handleSessionAdd} onSessionClick={handleSessionClick}
          onSessionUpdate={handleSessionUpdate} onSessionDelete={handleSessionDelete}
          onSessionDuplicate={handleSessionDuplicate} rowH={rowH} onRowHChange={setRowH}
          scrollRef={calendarScrollRef}
        />
      </Box>

      <SessionDrawer open={drawerOpen} session={editSession} event={event}
        onClose={() => setDrawerOpen(false)} onSave={handleDrawerSave}
        onDelete={editSession ? handleDrawerDelete : null} />
    </Box>
  );
});

export default EventScheduleTab;